import { Button } from '../../components/ui/button';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { InformationSchema, Ty, Parser } from '@cotera/era';
import { FieldErrors, useForm } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { useInScopeModel, useWatchableValue } from '../../actions/framework';
import { GraphModel } from '../../graph/graph.model';
import _, { startCase } from 'lodash';
import { useDuckDBQuery } from '@cotera/client/app/etc/data/duckdb';
import { useSchemas } from '../new-dataset';
import { useInvalidateArtifactById } from '@cotera/client/app/hooks/artifacts';
import { useWarehouseDialect } from '@cotera/client/app/hooks';
import { SQL } from '@cotera/era';
import { CsvUploadModel } from './csv-upload.model';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { Icon } from '@cotera/client/app/components/ui';

type Inputs = {
  datasetName: string;
  dbTableName: string;
};

const ERROR_MESSAGES: Record<string, string> = {
  'empty-csv-file': 'CSV file is empty',
  'exceeded-max-rows': `CSV file exceeds maximum supported rows`,
  'create-table-error':
    'Failed to create table. Please try again or contact support.',
  'parse-error': 'Failed to parse CSV file',
  'staging-error': 'Failed to stage dataset operations. Please try again.',
} as const;

export const ConfigureTable: React.FC<{
  initialName: string;
  headerRow: string[];
  dataRows: Ty.Scalar[][];
  onUpload?: (params: {
    preferredDatasetName: string;
    uploadedDbTableName: string;
    tableInfoSchemas: TableInfo[];
    columnInfoSchemas: ColumnInfo[];
    dialect: SQL.SqlDialect;
    refetchInfoSchemas: () => Promise<{
      tableInfoSchemas: TableInfo[];
      columnInfoSchemas: ColumnInfo[];
    }>;
  }) => void;
}> = ({ initialName, headerRow, dataRows, onUpload }) => {
  const baseName = initialName.replace('.csv', '');
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<Inputs>({
    mode: 'onChange',
    defaultValues: {
      datasetName: startCase(deriveDbTableName(baseName)),
      dbTableName: deriveDbTableName(baseName),
    },
  });

  const client = useTenantedClient();

  const dialect = useWarehouseDialect();
  const allSchemas = useSchemas();
  const existingGraphSymbols = useExistingGraphSymbols();
  const { existingDbTables, refetchInfoSchemas } =
    useRefetchInfoSchemas(allSchemas);
  const model = useInScopeModel(CsvUploadModel);
  const modelState = useWatchableValue(model.state);

  const errorMessages = getErrorMessages(errors);
  const modelErrors =
    modelState.t === 'await-upload' && modelState.errors
      ? modelState.errors
      : [];

  function onSubmit({ datasetName, dbTableName }: Inputs) {
    model.state.set({
      t: 'creating-table',
    });

    const headerRowWithRowNum = [...headerRow, '__row_number'];
    const dataRowsWithRowNum = dataRows.map((row, rowIdx) => [
      ...row,
      rowIdx.toString(), // Snowflake infers integer columns as floats and we can't use floats as PKs, so we have to use string.
    ]);

    // Transpose data to get columns, then infer types for each column
    const columnTypes = Object.fromEntries(
      headerRow.map((header, colIndex) => {
        const columnValues = dataRows.map((row) => row[colIndex] ?? null);

        const inferredType = Parser.inferType(columnValues, {
          maxVariantNumber: 0,
        });

        // Fall back to string if inference fails
        return [header, inferredType ?? Ty.ty('string')];
      })
    );

    const columnTypesWithRowNum: Record<string, Ty.ExtendedAttributeType> = {
      ...columnTypes,
      __row_number: Ty.ty({ k: 'primitive', t: 'string' }, { nullable: false }),
    };

    // createTable enqueues a job and returns immediately, so we need to poll
    // for the table to actually appear in the information schema
    const MAX_POLL_TIME_MS = 2 * 60 * 1000; // 2 minutes
    const POLL_INTERVAL_MS = 2000; // 2 seconds

    void (async () => {
      // Enqueue the table creation job
      await client.warehouse.connection.createTable({
        tableName: dbTableName,
        headerRow: headerRowWithRowNum,
        dataRows: dataRowsWithRowNum,
        columnTypes: columnTypesWithRowNum,
      });

      // Poll for the table to appear
      const startTime = Date.now();
      while (Date.now() - startTime < MAX_POLL_TIME_MS) {
        const { tableInfoSchemas, columnInfoSchemas } =
          await refetchInfoSchemas();

        const tableExists = tableInfoSchemas.some(
          (t) => t.table_name === dbTableName
        );

        if (tableExists) {
          onUpload?.({
            dialect,
            tableInfoSchemas,
            columnInfoSchemas,
            preferredDatasetName: datasetName,
            uploadedDbTableName: dbTableName,
            refetchInfoSchemas,
          });
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }

      // Timeout - table was not created
      model.state.set({
        t: 'await-upload',
        errors: [{ t: 'create-table-error' as const }],
      });
    })();
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      {modelErrors.length > 0 && (
        <FieldError
          className="mb-4"
          errors={modelErrors.map((e) => ({
            message: ERROR_MESSAGES[e.t] ?? 'An unexpected error occurred',
          }))}
        />
      )}

      <Field>
        <FieldLabel>Dataset Name</FieldLabel>
        <Input
          {...register('datasetName', {
            required: true,
            validate: (input) => {
              return existingGraphSymbols.includes(input)
                ? 'name already taken'
                : true;
            },
            onChange: (input: React.ChangeEvent<HTMLInputElement>) => {
              const value = input.target.value;
              setValue('dbTableName', deriveDbTableName(value), {
                shouldValidate: true,
              });
            },
          })}
        />
        <FieldError>{errorMessages.datasetName}</FieldError>
      </Field>

      <Collapsible>
        <div className="flex items-center justify-end w-full">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="text-muted-foreground" size="sm">
              Show Advanced Options{' '}
              <Icon icon="chevron-down" className="size-4" />
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <Field>
            <FieldLabel>Database Table Name</FieldLabel>
            <Input
              {...register('dbTableName', {
                required: true,
                validate: (input) =>
                  existingDbTables.includes(input)
                    ? 'database table name already taken'
                    : true,

                onChange: (input: React.ChangeEvent<HTMLInputElement>) => {
                  const value = input.target.value;

                  const sanitizedValue = value
                    // Remove all characters that are NOT letters, digits, or underscore
                    .replace(/[^a-zA-Z0-9_]/g, '');

                  setValue('dbTableName', sanitizedValue, {
                    shouldValidate: true,
                  });
                },
              })}
            />
            <FieldError>{errorMessages.dbTableName}</FieldError>
          </Field>
        </CollapsibleContent>
      </Collapsible>

      <Button
        type="submit"
        className={
          modelState.t === 'creating-table' ||
          modelState.t === 'verifying-table'
            ? 'animate-pulse'
            : ''
        }
        disabled={
          modelState.t === 'creating-table' ||
          modelState.t === 'verifying-table'
        }
      >
        {modelState.t === 'creating-table' && 'Creating...'}
        {modelState.t === 'verifying-table' &&
          `Verifying table (attempt ${modelState.attempt}/2)...`}
        {modelState.t !== 'creating-table' &&
          modelState.t !== 'verifying-table' &&
          'Create Dataset'}
      </Button>
    </form>
  );
};

/**
 * Returns the existing symbols in the graph.
 *
 * A new dataset should be named something that does not conflict with existing symbols.
 */
const useExistingGraphSymbols = (): string[] => {
  const graph = useInScopeModel(GraphModel);
  const buildExplorer = useWatchableValue(graph.graph);
  return buildExplorer.symbols;
};

export type TableInfo = {
  table_name: string;
  table_schema: string;
};

export type ColumnInfo = {
  table_name: string;
  table_schema: string;
  column_name: string;
  data_type: string;
  is_nullable: boolean;
};

const getErrorMessages = <Inputs extends Record<string, any>>(
  fieldErrors: FieldErrors<Inputs>
): {
  [FieldName in keyof Inputs]: string | null;
} => {
  return _.mapValues(fieldErrors, (fieldError) => {
    if (fieldError === undefined) {
      return null;
    }

    if (fieldError.message?.toString()) {
      return fieldError.message.toString();
    }

    if (fieldError.type === 'required') {
      return 'Required';
    }

    return null;
  });
};

/**
 * Given a candidate dataset name, derive sanitized database table name.
 */
export const deriveDbTableName = (datasetName: string): string =>
  datasetName
    // Convert to lowercase
    .toLowerCase()
    // Replace all whitespace with a single underscore
    .replace(/\s+/g, '_')
    // Replace all dashes with a single underscore
    .replace(/-+/g, '_')
    // Remove all characters that are NOT letters, digits, or underscore
    .replace(/[^a-zA-Z0-9_]/g, '')
    // Collapse multiple underscores into one (in case replacements create duplicates)
    .replace(/_+/g, '_')
    // Remove leading/trailing underscores
    .replace(/^_+|_+$/g, '');

function useRefetchInfoSchemas(selectedSchemas: string[]) {
  const invalidator = useInvalidateArtifactById();

  const tableNameSchemas = useDuckDBQuery({
    rel: InformationSchema({
      schemas: selectedSchemas,
      type: 'tables',
    }),
  });

  const columnSchemas = useDuckDBQuery({
    rel: InformationSchema({
      schemas: selectedSchemas,
      type: 'columns',
    }),
  });

  const tables = tableNameSchemas.data.data.toArray() as TableInfo[];
  const existingDbTables = tables.map((table) => table.table_name);

  const refetchInfoSchemas = async () => {
    await Promise.all([
      invalidator.mutateAsync({ id: columnSchemas.data.fromArtifactId }),
      invalidator.mutateAsync({ id: tableNameSchemas.data.fromArtifactId }),
    ]);

    const [freshTables, freshColumns] = await Promise.all([
      tableNameSchemas.refetch(),
      columnSchemas.refetch(),
    ]);

    const tableInfoSchemas = (freshTables.data?.data.toArray() ??
      []) as TableInfo[];

    const columnInfoSchemas = (freshColumns.data?.data.toArray() ??
      []) as ColumnInfo[];

    return {
      tableInfoSchemas,
      columnInfoSchemas,
    };
  };

  return {
    existingDbTables,
    refetchInfoSchemas,
  };
}
